/**
 * Help / documentation page for LetsWebinar.
 *
 * This page is the in-app user manual. Every major feature of the
 * project is documented here as a collapsible <Accordion> section so
 * users can scan the table of contents at the top, click into the
 * topic they need, and read step-by-step instructions without leaving
 * the application.
 *
 * Sections (in order):
 *   1.  Getting started (sign up / login / password reset)
 *   2.  Sidebar navigation overview
 *   3.  Home dashboard – managing webinars (all / active / inactive)
 *   4.  Creating a webinar (5-step wizard on the Events page)
 *   5.  Editing an existing webinar
 *   6.  Joining a webinar (registration link, query-string tags)
 *   7.  Watching a webinar (anytime, scrollable, chat, Q&A)
 *   8.  Webinar Analytics (sessions, registrants, filters, Excel)
 *   9.  Chat Reports
 *  10.  Ghost Users (manual + bulk Excel/Text upload)
 *  11.  Normal Users management (admin only)
 *  12.  Media Library – server <-> S3 transfer
 *  13.  Connect your AWS S3 bucket (full IAM walkthrough)
 *  14.  Settings (profile + change password)
 *  15.  Troubleshooting / FAQ
 */
import React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Chip,
  Divider,
  Link as MuiLink,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloudIcon from "@mui/icons-material/Cloud";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import DeleteIcon from "@mui/icons-material/Delete";
import SettingsIcon from "@mui/icons-material/Settings";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import HomeIcon from "@mui/icons-material/Home";
import ComputerIcon from "@mui/icons-material/Computer";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import PermMediaIcon from "@mui/icons-material/PermMedia";
import LiveTvIcon from "@mui/icons-material/LiveTv";
import QuizIcon from "@mui/icons-material/Quiz";
import VideocamIcon from "@mui/icons-material/Videocam";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import LoginIcon from "@mui/icons-material/Login";
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";
import EditIcon from "@mui/icons-material/Edit";
import LinkIcon from "@mui/icons-material/Link";
import MenuBookIcon from "@mui/icons-material/MenuBook";

import Layout from "../common/layout";

// ---------------------------------------------------------------------------
// Small inline helpers used across the help body so we keep the JSX clean.
// ---------------------------------------------------------------------------

/** Inline `<Code>some text</Code>` rendering. */
const Code = ({ children }) => (
  <Box
    component="code"
    sx={{
      px: 0.75,
      py: 0.25,
      borderRadius: 0.5,
      bgcolor: "action.hover",
      fontFamily: "monospace",
      fontSize: "0.85rem",
    }}
  >
    {children}
  </Box>
);

/** Pre-formatted block (preserves whitespace, used for JSON / URL samples). */
const Pre = ({ children }) => (
  <Box
    component="pre"
    sx={{
      mt: 1,
      mb: 1,
      p: 2,
      borderRadius: 1,
      bgcolor: "grey.100",
      fontFamily: "monospace",
      fontSize: "0.82rem",
      overflowX: "auto",
      whiteSpace: "pre-wrap",
    }}
  >
    {children}
  </Box>
);

/** Standard "step" list item with a green checkmark icon. */
const Step = ({ children }) => (
  <ListItem alignItems="flex-start">
    <ListItemIcon sx={{ minWidth: 32, mt: 0.5 }}>
      <CheckCircleOutlineIcon fontSize="small" color="success" />
    </ListItemIcon>
    <ListItemText primary={children} />
  </ListItem>
);

/** Header for a topic accordion – icon + title side by side. */
const TopicHeader = ({ icon, children }) => (
  <Stack direction="row" spacing={1} alignItems="center">
    {icon}
    <Typography variant="h6">{children}</Typography>
  </Stack>
);

function HelpPage() {
  return (
    <Layout showSideBar active={6}>
      <Box sx={{ p: { xs: 2, md: 4 }, width: "100%", maxWidth: 1040 }}>
        {/* ------------------------------------------------------------ */}
        {/* Page header                                                  */}
        {/* ------------------------------------------------------------ */}
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <HelpOutlineIcon color="primary" />
          <Typography variant="h5">Help &amp; Documentation</Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          A complete walkthrough of every feature in LetsWebinar – from
          signing up and creating your first webinar, all the way to S3
          storage, analytics and bulk ghost-user uploads. Use the table
          of contents below to jump straight to a topic, or scroll
          through and expand any section that interests you.
        </Typography>

        {/* ------------------------------------------------------------ */}
        {/* Table of contents                                            */}
        {/* ------------------------------------------------------------ */}
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <MenuBookIcon color="primary" fontSize="small" />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Table of contents
            </Typography>
          </Stack>
          <List dense disablePadding>
            <ListItem disableGutters>
              <ListItemText primary="1.  Getting started – sign up, log in & password reset" />
            </ListItem>
            <ListItem disableGutters>
              <ListItemText primary="2.  Sidebar navigation – what every icon does" />
            </ListItem>
            <ListItem disableGutters>
              <ListItemText primary="3.  Home dashboard – manage your webinars" />
            </ListItem>
            <ListItem disableGutters>
              <ListItemText primary="4.  Create a webinar – the 5-step Events wizard" />
            </ListItem>
            <ListItem disableGutters>
              <ListItemText primary="5.  Edit an existing webinar" />
            </ListItem>
            <ListItem disableGutters>
              <ListItemText primary="6.  Join a webinar (registration link & URL tags)" />
            </ListItem>
            <ListItem disableGutters>
              <ListItemText primary="7.  Watch room – anytime, scrollable, chat & participants" />
            </ListItem>
            <ListItem disableGutters>
              <ListItemText primary="8.  Webinar Analytics – sessions, registrants, filters" />
            </ListItem>
            <ListItem disableGutters>
              <ListItemText primary="9.  Chat Reports" />
            </ListItem>
            <ListItem disableGutters>
              <ListItemText primary="10. Ghost users – manual entry + bulk Excel/Text upload" />
            </ListItem>
            <ListItem disableGutters>
              <ListItemText primary="11. Normal Users management (admin only)" />
            </ListItem>
            <ListItem disableGutters>
              <ListItemText primary="12. Media library – server ↔ S3 transfer & playback" />
            </ListItem>
            <ListItem disableGutters>
              <ListItemText primary="13. Connect your AWS S3 bucket (full IAM walkthrough)" />
            </ListItem>
            <ListItem disableGutters>
              <ListItemText primary="14. Settings – profile & change password" />
            </ListItem>
            <ListItem disableGutters>
              <ListItemText primary="15. Troubleshooting / FAQ" />
            </ListItem>
          </List>
        </Paper>

        {/* ============================================================ */}
        {/* 1. Getting started                                           */}
        {/* ============================================================ */}
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <TopicHeader icon={<LoginIcon color="primary" />}>
              1. Getting started – sign up, log in &amp; password reset
            </TopicHeader>
          </AccordionSummary>
          <AccordionDetails>
            <Typography sx={{ mb: 1 }}>
              LetsWebinar accounts come in two roles: <b>admin</b> (can
              create webinars, manage normal users, configure S3, and
              access the Media tab) and <b>normal user</b> (can register
              and join webinars, view their own events).
            </Typography>

            <Typography variant="subtitle1" sx={{ mt: 2 }}>
              Create an account
            </Typography>
            <List dense>
              <Step>
                Go to <Code>/signup</Code> from the landing page or click
                <b> Sign up</b> in the header.
              </Step>
              <Step>
                Fill in <b>First name</b>, <b>Last name</b>,{" "}
                <b>Email</b> (must be a valid email format),{" "}
                <b>Phone number</b> (10 digits, numeric only),{" "}
                <b>Password</b> (min. 8 characters) and{" "}
                <b>Confirm password</b>.
              </Step>
              <Step>
                Tick the <i>terms &amp; conditions</i> checkbox and click
                <b> Sign up</b>. On success you’re logged in
                automatically and redirected to <Code>/home</Code>.
              </Step>
            </List>

            <Typography variant="subtitle1" sx={{ mt: 2 }}>
              Log in / log out
            </Typography>
            <List dense>
              <Step>
                On <Code>/login</Code> enter your email and password and
                click <b>Login</b>. Your JWT access &amp; refresh tokens
                are saved to <Code>localStorage</Code>.
              </Step>
              <Step>
                To log out, click your avatar in the top-right corner and
                pick <b>Logout</b> (or <b>Logout</b> at the bottom of the
                sidebar). The refresh token is invalidated server-side.
              </Step>
            </List>

            <Typography variant="subtitle1" sx={{ mt: 2 }}>
              Forgot your password?
            </Typography>
            <List dense>
              <Step>
                Click <b>Forgot password?</b> on the login screen. You’ll
                land on <Code>/recoverPassword</Code>.
              </Step>
              <Step>
                Type your account email. We’ll send a one-time reset link
                styled like
                <Code>
                  /forgetPassword/&lt;userId&gt;/&lt;userToken&gt;
                </Code>{" "}
                to your inbox.
              </Step>
              <Step>
                Open the link, choose a new password and confirm. You’ll
                be redirected to login.
              </Step>
            </List>

            <Alert severity="info" sx={{ mt: 2 }}>
              Reset links expire fast for security – if you don’t use the
              link within ~24h, request a new one.
            </Alert>
          </AccordionDetails>
        </Accordion>

        {/* ============================================================ */}
        {/* 2. Sidebar navigation                                        */}
        {/* ============================================================ */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <TopicHeader icon={<MenuBookIcon color="primary" />}>
              2. Sidebar navigation – what every icon does
            </TopicHeader>
          </AccordionSummary>
          <AccordionDetails>
            <Typography sx={{ mb: 1 }}>
              The left rail (or hamburger menu on mobile) is the entry
              point to every part of the app. The exact set of icons
              depends on your role.
            </Typography>

            <Typography variant="subtitle2" sx={{ mt: 1 }}>
              Admin sidebar
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon><HomeIcon color="primary" /></ListItemIcon>
                <ListItemText
                  primary="Home"
                  secondary={<>Dashboard with your webinars (<Code>/home</Code>).</>}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><ComputerIcon color="primary" /></ListItemIcon>
                <ListItemText
                  primary="Events"
                  secondary={<>5-step wizard to create a new webinar (<Code>/events</Code>).</>}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><PermMediaIcon color="primary" /></ListItemIcon>
                <ListItemText
                  primary="Media"
                  secondary={<>Per-user S3 media library (<Code>/media</Code>) – admin only.</>}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><PeopleAltIcon color="primary" /></ListItemIcon>
                <ListItemText
                  primary="Users"
                  secondary={<>Manage normal users (<Code>/users</Code>) – admin only.</>}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><GroupAddIcon color="primary" /></ListItemIcon>
                <ListItemText
                  primary="Ghost users"
                  secondary={<>Synthetic chat-bot users (<Code>/webinarGhostUser</Code>).</>}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><SettingsIcon color="primary" /></ListItemIcon>
                <ListItemText
                  primary="Settings"
                  secondary={<>Profile + change password (<Code>/setting</Code>).</>}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><HelpOutlineIcon color="primary" /></ListItemIcon>
                <ListItemText
                  primary="Help"
                  secondary={<>This documentation page (<Code>/help</Code>).</>}
                />
              </ListItem>
            </List>

            <Typography variant="subtitle2" sx={{ mt: 2 }}>
              Normal-user sidebar
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Same as above but the <b>Media</b>, <b>Users</b> and{" "}
              <b>Help</b> entries are hidden – they’re only meaningful
              for admins.
            </Typography>
          </AccordionDetails>
        </Accordion>

        {/* ============================================================ */}
        {/* 3. Home dashboard                                            */}
        {/* ============================================================ */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <TopicHeader icon={<HomeIcon color="primary" />}>
              3. Home dashboard – manage your webinars
            </TopicHeader>
          </AccordionSummary>
          <AccordionDetails>
            <Typography sx={{ mb: 1 }}>
              The Home page (<Code>/home</Code>) shows three vertical
              tabs:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText
                  primary="All Events"
                  secondary="Every webinar you own, regardless of status."
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Active"
                  secondary="Webinars whose status is currently 'active' (scheduled or live)."
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="InActive"
                  secondary="Webinars you have paused / archived."
                />
              </ListItem>
            </List>

            <Typography variant="subtitle1" sx={{ mt: 2 }}>
              What you can do from each row
            </Typography>
            <List dense>
              <Step>
                <b>Edit</b> – opens <Code>/webinarEdit/&lt;id&gt;</Code>{" "}
                so you can re-run the wizard against an existing webinar
                (see section 5).
              </Step>
              <Step>
                <b>View / Join</b> – opens the watch room
                <Code>/webinarView/&lt;id&gt;</Code> or the registration
                page <Code>/webinarJoin/&lt;id&gt;</Code> depending on
                whether you’re logged in.
              </Step>
              <Step>
                <b>Analytics</b> – jumps to{" "}
                <Code>/webinarAnalytics/&lt;id&gt;</Code> (sessions and
                registrants tables).
              </Step>
              <Step>
                <b>Chat report</b> – opens{" "}
                <Code>/chatReport/&lt;id&gt;</Code> with every chat
                message captured during the webinar.
              </Step>
              <Step>
                <b>Delete</b> – soft-deletes the webinar. It will no
                longer appear in any list but the underlying analytics
                data is preserved on the server.
              </Step>
            </List>

            <Alert severity="info" sx={{ mt: 2 }}>
              Tip: You can share the join URL directly. The format is
              <Code>
                /webinarJoin/&lt;webinarId&gt;?name=…&amp;email=…&amp;mobile=…
              </Code>{" "}
              – see section 6 for tag-based pre-fill and tracking.
            </Alert>
          </AccordionDetails>
        </Accordion>

        {/* ============================================================ */}
        {/* 4. Create a webinar – Events wizard                          */}
        {/* ============================================================ */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <TopicHeader icon={<ComputerIcon color="primary" />}>
              4. Create a webinar – the 5-step Events wizard
            </TopicHeader>
          </AccordionSummary>
          <AccordionDetails>
            <Typography sx={{ mb: 1 }}>
              Click <b>Events</b> in the sidebar (or visit{" "}
              <Code>/events</Code>). A draft webinar is created in the
              background as soon as the page loads, so you can resume
              even if you refresh halfway. The wizard has 5 tabs that
              advance via the <b>Next</b> button at the bottom.
            </Typography>

            <Typography variant="subtitle1" sx={{ mt: 1 }}>
              Step 1 – Details
            </Typography>
            <List dense>
              <Step>
                <b>Name</b> – internal name of the webinar (3+ chars).
              </Step>
              <Step>
                <b>Title</b> – the title attendees see in the watch room
                (3+ chars).
              </Step>
            </List>

            <Typography variant="subtitle1" sx={{ mt: 2 }}>
              Step 2 – Video <VideocamIcon fontSize="inherit" />
            </Typography>
            <Typography sx={{ mb: 1 }}>
              Pick exactly one video source for this webinar:
            </Typography>
            <List dense>
              <Step>
                <b>URL link</b> – paste any direct video URL (mp4 / HLS /
                stream).
              </Step>
              <Step>
                <b>Upload video</b> – uploads from your computer using
                the <i>chunked uploader</i> (5&nbsp;MB chunks). Files of
                any size work because each request stays well under the
                100&nbsp;MB Cloudflare cap; you’ll see a percent-progress
                bar while it transfers.
              </Step>
              <Step>
                <b>Choose from uploaded list</b> – re-use a video you
                already uploaded earlier.
              </Step>
              <Step>
                <b>Choose from directory</b> – pick a video that admins
                placed under <Code>media/directory_video/</Code>.
              </Step>
            </List>

            <Typography variant="subtitle1" sx={{ mt: 2 }}>
              Step 3 – Live <LiveTvIcon fontSize="inherit" />
            </Typography>
            <List dense>
              <Step>
                <b>Schedule date &amp; time</b> – pick a one-off start
                time using the date-time picker (saved as UTC, displayed
                in the user’s local timezone).
              </Step>
              <Step>
                <b>Recurrence</b> – instead of a single time, enable one
                of <i>Daily</i>, <i>Weekly</i> or <i>Monthly</i> for a
                recurring webinar.
              </Step>
              <Step>
                <b>Resolution status</b> – tick which playback
                resolutions (240p / 480p / 720p / 1080p) the watch room
                should expose to viewers.
              </Step>
              <Step>
                <b>Anytime webinar</b> – when on, viewers can join at
                any time and the player resumes from where they last
                stopped (cookie-based, per webinar id, 1-year TTL).
              </Step>
              <Step>
                <b>Allow restart on anytime</b> – shows a “Start from
                beginning” button next to <i>Leave</i> for anytime
                viewers. Useful if you want viewers to be able to wipe
                their saved progress.
              </Step>
              <Step>
                <b>Scrollable webinar</b> – exposes native browser video
                controls so attendees can scrub the timeline. Off by
                default to preserve the locked-down playback experience.
              </Step>
            </List>

            <Typography variant="subtitle1" sx={{ mt: 2 }}>
              Step 4 – Ghost users <GroupAddIcon fontSize="inherit" />
            </Typography>
            <Typography sx={{ mb: 1 }}>
              Pick which ghost users should join the chat for this
              webinar. The full ghost-user library is managed under
              <b> Ghost users</b> in the sidebar (section 10).
            </Typography>
            <List dense>
              <Step>
                Tick the rows you want to associate with this webinar.
              </Step>
              <Step>
                The selection is saved when you click <b>Next</b> – it
                becomes the “bot pool” the system uses to drive Q&amp;A
                in step 5.
              </Step>
            </List>

            <Typography variant="subtitle1" sx={{ mt: 2 }}>
              Step 5 – Questions &amp; Answers <QuizIcon fontSize="inherit" />
            </Typography>
            <List dense>
              <Step>
                Click <b>Add question</b>. Enter the question text and
                pick a <b>time</b> (HH:MM:SS) – this is when the question
                will be auto-posted in the live chat by a ghost user.
              </Step>
              <Step>
                Add one or more <b>Answers</b> per question. Each answer
                gets its own time and ghost-user attribution. The system
                cycles through the assigned ghost users so the chat feels
                organic.
              </Step>
              <Step>
                Use the trash-can icons to delete a question or any of
                its answers.
              </Step>
              <Step>
                Click <b>Create</b> – you’re returned to the Home
                dashboard with the new webinar live.
              </Step>
            </List>

            <Alert severity="info" sx={{ mt: 2 }}>
              Use <b>Prev</b> at any time to revisit an earlier tab.
              Tabs are disabled until you reach them with <b>Next</b> so
              you can’t skip required validation.
            </Alert>
          </AccordionDetails>
        </Accordion>

        {/* ============================================================ */}
        {/* 5. Edit an existing webinar                                  */}
        {/* ============================================================ */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <TopicHeader icon={<EditIcon color="primary" />}>
              5. Edit an existing webinar
            </TopicHeader>
          </AccordionSummary>
          <AccordionDetails>
            <Typography sx={{ mb: 1 }}>
              From the Home dashboard click the pencil icon on any
              webinar row, or open{" "}
              <Code>/webinarEdit/&lt;webinarId&gt;</Code> directly.
            </Typography>
            <List dense>
              <Step>
                The same 5 tabs from the Events wizard are pre-filled
                with the webinar’s current values.
              </Step>
              <Step>
                Replace the video by going back to step 2 – the existing
                file is shown with a <b>Delete</b> icon so you can swap
                it for a new upload, link, or directory pick.
              </Step>
              <Step>
                Toggle the <b>Anytime</b>, <b>Allow restart</b> and{" "}
                <b>Scrollable</b> switches in step 3 to update the
                viewing experience without re-uploading anything.
              </Step>
              <Step>
                Edits save tab-by-tab when you click <b>Next</b>; click
                <b> Update</b> on the last tab to commit and return to
                Home.
              </Step>
            </List>
          </AccordionDetails>
        </Accordion>

        {/* ============================================================ */}
        {/* 6. Joining a webinar                                         */}
        {/* ============================================================ */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <TopicHeader icon={<LinkIcon color="primary" />}>
              6. Join a webinar (registration link &amp; URL tags)
            </TopicHeader>
          </AccordionSummary>
          <AccordionDetails>
            <Typography sx={{ mb: 1 }}>
              Every webinar exposes a public registration page. Share
              this URL with your audience:
            </Typography>
            <Pre>{`/webinarJoin/<webinarId>`}</Pre>

            <Typography variant="subtitle1" sx={{ mt: 1 }}>
              Pre-filling the form
            </Typography>
            <Typography sx={{ mb: 1 }}>
              You can append query-string parameters to skip data entry:
            </Typography>
            <Pre>{`/webinarJoin/<webinarId>?name=YourName&mobile=YourMobileNumber&email=YourEmailAddress`}</Pre>

            <Typography variant="subtitle1" sx={{ mt: 2 }}>
              Marketing tags (UTM-style)
            </Typography>
            <Typography sx={{ mb: 1 }}>
              Add any of these tags – they’re stored against the
              registrant and show up in the Analytics page so you can
              segment your traffic sources:
            </Typography>
            <Pre>{`?tag=webinar_tag
&tag_user=user123
&tag_company=companyX
&tag_custom=customTag1
&tag_source=google_ads
&tag_medium=social_media
&tag_campaign=spring_campaign
&tag_content=video_session
&tag_term=technology`}</Pre>

            <Typography variant="subtitle1" sx={{ mt: 2 }}>
              What the registrant sees
            </Typography>
            <List dense>
              <Step>
                A registration form (Name, Email, Mobile). Required
                fields are validated client-side.
              </Step>
              <Step>
                On submit, a guest session is created and they’re
                redirected to <Code>/webinarView/&lt;id&gt;</Code> when
                the webinar is live, or to{" "}
                <Code>/webinarAttendSuccessfully/&lt;id&gt;</Code>{" "}
                otherwise (with the scheduled time displayed).
              </Step>
              <Step>
                Reminder emails are sent automatically (1 day, 3 hours,
                etc.) using the templates under{" "}
                <Code>webinar/templates/webinar/email/</Code>.
              </Step>
            </List>
          </AccordionDetails>
        </Accordion>

        {/* ============================================================ */}
        {/* 7. Watch room                                                */}
        {/* ============================================================ */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <TopicHeader icon={<PlayArrowIcon color="primary" />}>
              7. Watch room – anytime, scrollable, chat &amp; participants
            </TopicHeader>
          </AccordionSummary>
          <AccordionDetails>
            <Typography sx={{ mb: 1 }}>
              The watch room (<Code>/webinarView/&lt;id&gt;</Code>) is
              an HLS player on the left and a tabbed sidebar on the
              right.
            </Typography>

            <Typography variant="subtitle1" sx={{ mt: 1 }}>
              Player behaviour
            </Typography>
            <List dense>
              <Step>
                Plays HLS via the bundled <Code>hls.js</Code> – works in
                strict-CSP / air-gapped environments.
              </Step>
              <Step>
                <b>Anytime webinar</b>: every 5&nbsp;seconds the
                viewer’s position is written to a per-webinar cookie
                (<Code>lw_webinar_progress_&lt;id&gt;</Code>, 1-year
                TTL). When they reload they resume from the saved
                second. Click <b>Start from beginning</b> (top-right) to
                wipe the cookie and restart at 0 – only visible when the
                admin enabled <i>Allow restart on anytime</i>.
              </Step>
              <Step>
                <b>Scrollable webinar</b>: native browser controls are
                exposed so attendees can scrub. Off by default for live
                broadcasts.
              </Step>
              <Step>
                <b>Live (scheduled) webinar</b>: the player only
                unblocks at the scheduled time. Before that, the user
                sees a “Webinar starts in …” countdown.
              </Step>
            </List>

            <Typography variant="subtitle1" sx={{ mt: 2 }}>
              Sidebar tabs
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon>
                  <PeopleAltIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary="Participants"
                  secondary="Live count and list of viewers + ghost users currently in the room. Updated via WebSocket."
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <ChatBubbleOutlineIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary="Chats"
                  secondary="Send a chat message via the input at the bottom; messages from ghost users (and scheduled Q&A from step 5 of the wizard) appear automatically."
                />
              </ListItem>
            </List>

            <Typography variant="subtitle1" sx={{ mt: 2 }}>
              Leaving a webinar
            </Typography>
            <Typography variant="body2" color="text.secondary">
              The <b>Leave</b> button (top-right) ends the guest session
              and redirects back to the registration page. Closing the
              browser tab also fires a leave event over the WebSocket so
              the participant count updates instantly.
            </Typography>
          </AccordionDetails>
        </Accordion>

        {/* ============================================================ */}
        {/* 8. Analytics                                                 */}
        {/* ============================================================ */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <TopicHeader icon={<AnalyticsIcon color="primary" />}>
              8. Webinar Analytics – sessions, registrants, filters
            </TopicHeader>
          </AccordionSummary>
          <AccordionDetails>
            <Typography sx={{ mb: 1 }}>
              Open the analytics page from any webinar row, or via{" "}
              <Code>/webinarAnalytics/&lt;webinarId&gt;</Code>. Two
              tables stack vertically:
            </Typography>

            <Typography variant="subtitle1" sx={{ mt: 1 }}>
              Sessions table
            </Typography>
            <List dense>
              <Step>
                One row per <i>session</i> (a discrete time the webinar
                was actually started/streamed).
              </Step>
              <Step>
                Click a session id to drill into that run’s
                participants.
              </Step>
              <Step>
                <b>Download Excel</b> exports every session for the
                webinar.
              </Step>
            </List>

            <Typography variant="subtitle1" sx={{ mt: 2 }}>
              Registrants table
            </Typography>
            <List dense>
              <Step>
                Each row is one registrant: name, email, mobile, when
                they joined, how long they watched, and the percentage
                of the video they completed.
              </Step>
              <Step>
                <b>Search</b> – type in the search box to filter by
                name/email/mobile (server-side, paginated).
              </Step>
              <Step>
                <b>Filter by % watched</b> – drop-down with ranges (e.g.{" "}
                <Code>0-25</Code>, <Code>25-50</Code>, …). Useful to
                isolate hot leads (90-100%) or no-shows (0%).
              </Step>
              <Step>
                <b>UTM tags</b> – any tag values passed in the join URL
                (section 6) are visible per-row.
              </Step>
              <Step>
                <b>Reset</b> clears search + filter and re-loads the full
                paginated list.
              </Step>
              <Step>
                <b>Download Excel</b> exports every registrant in a
                single sheet for offline analysis.
              </Step>
            </List>
          </AccordionDetails>
        </Accordion>

        {/* ============================================================ */}
        {/* 9. Chat reports                                              */}
        {/* ============================================================ */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <TopicHeader icon={<ChatBubbleOutlineIcon color="primary" />}>
              9. Chat Reports
            </TopicHeader>
          </AccordionSummary>
          <AccordionDetails>
            <Typography sx={{ mb: 1 }}>
              At <Code>/chatReport/&lt;webinarId&gt;</Code> you get a
              chronological table of every message that ever flowed
              through the webinar’s chat – real users <i>and</i> ghost
              users, with the timestamp at which it was sent.
            </Typography>
            <List dense>
              <Step>
                Useful for post-event review: spot questions you missed,
                build FAQ content, or audit ghost-user activity.
              </Step>
              <Step>
                The page is read-only – to remove chat content edit the
                webinar’s Q&amp;A in step 5 of the wizard, or delete the
                ghost user that posted it.
              </Step>
            </List>
          </AccordionDetails>
        </Accordion>

        {/* ============================================================ */}
        {/* 10. Ghost users                                              */}
        {/* ============================================================ */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <TopicHeader icon={<GroupAddIcon color="primary" />}>
              10. Ghost users – manual entry &amp; bulk Excel/Text upload
            </TopicHeader>
          </AccordionSummary>
          <AccordionDetails>
            <Typography sx={{ mb: 1 }}>
              Ghost users are synthetic chatters that make a webinar
              feel populated and drive scripted Q&amp;A. Manage them at{" "}
              <Code>/webinarGhostUser</Code>.
            </Typography>

            <Typography variant="subtitle1" sx={{ mt: 1 }}>
              Add a single ghost user
            </Typography>
            <List dense>
              <Step>
                Click <b>Add Ghost User</b>. Enter <b>First name</b> and{" "}
                <b>Last name</b> – both are required.
              </Step>
              <Step>
                Click <b>Add User</b>. The new ghost is now available in
                step 4 of the Events wizard for any new webinar.
              </Step>
            </List>

            <Typography variant="subtitle1" sx={{ mt: 2 }}>
              Bulk upload from Excel / CSV
            </Typography>
            <List dense>
              <Step>
                Click <b>Upload Excel</b> and pick a <Code>.xlsx</Code>,{" "}
                <Code>.xls</Code> or <Code>.csv</Code> file.
              </Step>
              <Step>
                Required columns:{" "}
                <Code>first_name, last_name</Code>. Optional columns:{" "}
                <Code>email, phone_number</Code>. The first row must be
                the header.
              </Step>
              <Step>
                You’ll see a toast like “12 ghost user(s) added
                successfully!” and the table refreshes.
              </Step>
            </List>

            <Typography variant="subtitle1" sx={{ mt: 2 }}>
              Bulk upload from a plain-text file
            </Typography>
            <List dense>
              <Step>
                Click <b>Upload Text</b> and pick a <Code>.txt</Code>{" "}
                file.
              </Step>
              <Step>
                One user per line. Either of these formats works:
              </Step>
            </List>
            <Pre>{`John Doe
Jane Smith, smith@example.com
Sam, Lee, sam@x.com, 9876543210`}</Pre>

            <Typography variant="subtitle1" sx={{ mt: 2 }}>
              Search &amp; edit
            </Typography>
            <List dense>
              <Step>
                Use the search box to filter by full name (server-side
                search, paginated).
              </Step>
              <Step>
                Click the pencil icon on any row to update its name.
              </Step>
            </List>

            <Alert severity="info" sx={{ mt: 2 }}>
              A ghost user can be reused across as many webinars as you
              like – associations are made per-webinar in step 4 of the
              Events wizard.
            </Alert>
          </AccordionDetails>
        </Accordion>

        {/* ============================================================ */}
        {/* 11. Normal users management                                  */}
        {/* ============================================================ */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <TopicHeader icon={<PersonAddAltIcon color="primary" />}>
              11. Normal Users management (admin only)
            </TopicHeader>
          </AccordionSummary>
          <AccordionDetails>
            <Typography sx={{ mb: 1 }}>
              The <b>Users</b> sidebar entry (visible only to admins,
              path <Code>/users</Code>) lists every registered normal
              user. From here you can:
            </Typography>
            <List dense>
              <Step>
                <b>Search</b> by name / email.
              </Step>
              <Step>
                <b>Create a user</b> – fill First / Last / Email / Phone
                and a password. Tick <i>Activate user</i> to make the
                account usable immediately.
              </Step>
              <Step>
                <b>Edit a user</b> – click the pencil icon to update
                their details, or use the toggle to activate /
                deactivate the account without deleting it.
              </Step>
              <Step>
                Pagination at the bottom navigates the full list 10 rows
                at a time.
              </Step>
            </List>
            <Alert severity="warning" sx={{ mt: 2 }}>
              Deactivated users cannot log in or join webinars but their
              registration history is retained for analytics.
            </Alert>
          </AccordionDetails>
        </Accordion>

        {/* ============================================================ */}
        {/* 12. Media library – server <-> S3                            */}
        {/* ============================================================ */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <TopicHeader icon={<CloudUploadIcon color="primary" />}>
              12. Media library – server ↔ S3 transfer &amp; playback
            </TopicHeader>
          </AccordionSummary>
          <AccordionDetails>
            <Typography sx={{ mb: 2 }}>
              The Media tab (<Code>/media</Code>, admin only) shows
              every uploaded and directory video for your account. Each
              row tells you exactly where the file lives:
            </Typography>

            <List>
              <ListItem>
                <ListItemIcon><Chip size="small" label="server" color="primary" /></ListItemIcon>
                <ListItemText
                  primary="Server only"
                  secondary="The file is on the LetsWebinar server's local disk only. Processing (ffmpeg/HLS) always happens here."
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><Chip size="small" label="s3" color="info" /></ListItemIcon>
                <ListItemText
                  primary="S3 only"
                  secondary="The file lives in your S3 bucket; the server's local disk has been freed. You can pull it back to the server with the cloud-download icon."
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><Chip size="small" label="both" color="success" /></ListItemIcon>
                <ListItemText
                  primary="Both"
                  secondary="The video is mirrored on the server AND in S3. Playback uses your preferred source (set with the dropdown at the top) and silently falls back to the other location if it's unavailable."
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><Chip size="small" label="missing" /></ListItemIcon>
                <ListItemText
                  primary="Missing"
                  secondary="No copy can be located. The row will be soft-deleted when you remove the last copy."
                />
              </ListItem>
            </List>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle1">Action icons explained</Typography>
            <List dense>
              <ListItem>
                <ListItemIcon><PlayArrowIcon color="primary" /></ListItemIcon>
                <ListItemText
                  primary="Play"
                  secondary="Opens the best playback URL in a new tab. The 'Prefer source' dropdown decides whether it tries the server or S3 first; the other source is used as a fallback."
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><CloudUploadIcon color="primary" /></ListItemIcon>
                <ListItemText
                  primary="Transfer to S3"
                  secondary="Uploads the local file to your S3 bucket and switches the row to 'both' so you can still play it from the server. Disabled if the video has no local copy or if you haven't configured S3 yet."
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><CloudDownloadIcon color="primary" /></ListItemIcon>
                <ListItemText
                  primary="Pull back from S3"
                  secondary="Downloads the S3 object back onto the LetsWebinar server. Useful before re-running ffmpeg processing or before serving to a large live audience."
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><DeleteIcon color="warning" /></ListItemIcon>
                <ListItemText
                  primary="Remove server copy"
                  secondary="Deletes the file from the LetsWebinar server only. Safe to use after a successful upload to S3 if you want to free disk space."
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><DeleteIcon color="error" /></ListItemIcon>
                <ListItemText
                  primary="Remove S3 copy"
                  secondary="Deletes the object from your S3 bucket. The local file is untouched. If both copies are gone the row is automatically soft-deleted."
                />
              </ListItem>
            </List>

            <Alert severity="info" sx={{ mt: 2 }}>
              <b>Processing rule:</b> ffmpeg runs only on the LetsWebinar
              server. If you've already moved a file to S3 only, click{" "}
              <i>Pull back from S3</i> first, run processing from the
              webinar/event editor, and then move it back to S3 if you
              want to free disk space again.
            </Alert>

            <Alert severity="info" sx={{ mt: 2 }}>
              The <b>Refresh</b> button at the top regenerates the
              presigned URLs in the table (they’re valid for 6 hours).
              Use it if a Play link starts returning <Code>403</Code>.
            </Alert>
          </AccordionDetails>
        </Accordion>

        {/* ============================================================ */}
        {/* 13. AWS S3 setup                                             */}
        {/* ============================================================ */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <TopicHeader icon={<CloudIcon color="primary" />}>
              13. Connect your AWS S3 bucket (full IAM walkthrough)
            </TopicHeader>
          </AccordionSummary>
          <AccordionDetails>
            <Typography sx={{ mb: 2 }}>
              Each user/account configures <b>their own</b> AWS S3
              bucket. LetsWebinar uses those credentials to upload
              finished videos from this server into your bucket, pull
              them back when needed, and stream them to attendees with
              automatic fallback to the server copy if S3 is
              unavailable.
            </Typography>

            <Alert severity="info" sx={{ mb: 2 }}>
              Your secret access key is stored <b>encrypted</b> on the
              server (Fernet) and is never sent back to the browser. It
              is only decrypted in-memory when LetsWebinar talks to AWS
              on your behalf.
            </Alert>

            <Typography variant="subtitle1" sx={{ mt: 2 }}>
              1. Create the S3 bucket
            </Typography>
            <List dense>
              <Step>
                Sign in to{" "}
                <MuiLink
                  href="https://s3.console.aws.amazon.com/s3/home"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  the AWS S3 console
                </MuiLink>{" "}
                and click <b>Create bucket</b>.
              </Step>
              <Step>
                Pick a globally unique <Code>bucket name</Code> (e.g.{" "}
                <Code>my-company-letswebinar</Code>) and the AWS{" "}
                <Code>region</Code> closest to your audience (e.g.{" "}
                <Code>us-east-1</Code> or <Code>ap-south-1</Code>).
              </Step>
              <Step>
                You can keep <b>Block all public access</b> turned on –
                LetsWebinar only ever uses time-limited{" "}
                <i>presigned URLs</i> for playback, never public links.
              </Step>
            </List>

            <Typography variant="subtitle1" sx={{ mt: 2 }}>
              2. Create an IAM user with the right permissions
            </Typography>
            <List dense>
              <Step>
                Open the <b>IAM</b> console → <b>Users</b> →{" "}
                <b>Create user</b>. Give it programmatic access only (no
                console login needed).
              </Step>
              <Step>
                Attach an inline policy that scopes the user to your
                bucket only:
              </Step>
            </List>
            <Pre>{`{
  "Version": "2012-10-17",
  "Statement": [
    {
      "Sid": "LetsWebinarBucketAccess",
      "Effect": "Allow",
      "Action": [
        "s3:ListBucket",
        "s3:GetBucketLocation"
      ],
      "Resource": "arn:aws:s3:::YOUR-BUCKET-NAME"
    },
    {
      "Sid": "LetsWebinarObjectAccess",
      "Effect": "Allow",
      "Action": [
        "s3:GetObject",
        "s3:PutObject",
        "s3:DeleteObject",
        "s3:AbortMultipartUpload"
      ],
      "Resource": "arn:aws:s3:::YOUR-BUCKET-NAME/*"
    }
  ]
}`}</Pre>
            <Typography variant="caption" color="text.secondary">
              Replace <Code>YOUR-BUCKET-NAME</Code> with the real bucket
              name you picked in step 1.
            </Typography>

            <List dense>
              <Step>
                Once the user is created, open it →{" "}
                <b>Security credentials</b> → <b>Create access key</b> →
                choose <i>Application running outside AWS</i>. Copy the{" "}
                <Code>Access key ID</Code> and{" "}
                <Code>Secret access key</Code> – AWS will only show the
                secret once.
              </Step>
            </List>

            <Typography variant="subtitle1" sx={{ mt: 2 }}>
              3. (Optional) CORS so the browser can stream from S3
            </Typography>
            <Typography sx={{ mb: 1 }}>
              The Media tab plays videos via presigned URLs. For most
              browsers this works without any CORS config because the
              URL is opened in a new tab. If you ever want to embed the
              S3 URL inside the webinar player itself, add this CORS
              rule on the bucket (<b>Permissions</b> → <b>CORS</b>):
            </Typography>
            <Pre>{`[
  {
    "AllowedHeaders": ["*"],
    "AllowedMethods": ["GET", "HEAD"],
    "AllowedOrigins": ["https://your-letswebinar-domain.com"],
    "ExposeHeaders": ["ETag", "Content-Length", "Content-Range"],
    "MaxAgeSeconds": 3000
  }
]`}</Pre>

            <Typography variant="subtitle1" sx={{ mt: 2 }}>
              4. Save the credentials in LetsWebinar
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <SettingsIcon fontSize="small" color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <>
                      In the sidebar open the <b>Media</b> tab and click{" "}
                      <b>S3 Settings</b>.
                    </>
                  }
                />
              </ListItem>
              <Step>
                Fill in <Code>bucket name</Code>, <Code>region</Code>,{" "}
                <Code>access key ID</Code>, and{" "}
                <Code>secret access key</Code> (paste the values you
                copied in step 2). Optionally set a folder{" "}
                <Code>prefix</Code> – every uploaded file will land in{" "}
                <Code>{"<bucket>/<prefix>/…"}</Code>. Default is{" "}
                <Code>letswebinar</Code>.
              </Step>
              <Step>
                Click <b>Save</b>, then <b>Test connection</b>. A
                successful test means LetsWebinar can call{" "}
                <Code>HeadBucket</Code> on your bucket – you’re done.
              </Step>
            </List>

            <Alert severity="warning" sx={{ mt: 2 }}>
              The <i>Custom endpoint URL</i> field is only needed for
              S3-compatible providers like DigitalOcean Spaces, Wasabi,
              MinIO or Cloudflare R2. Leave it blank for native AWS S3.
            </Alert>
          </AccordionDetails>
        </Accordion>

        {/* ============================================================ */}
        {/* 14. Settings                                                 */}
        {/* ============================================================ */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <TopicHeader icon={<VpnKeyIcon color="primary" />}>
              14. Settings – profile &amp; change password
            </TopicHeader>
          </AccordionSummary>
          <AccordionDetails>
            <Typography sx={{ mb: 1 }}>
              Open <Code>/setting</Code> from the sidebar.
            </Typography>

            <Typography variant="subtitle1" sx={{ mt: 1 }}>
              My Details tab
            </Typography>
            <List dense>
              <Step>
                Update your first name, last name, email and phone
                number. Saving updates the JWT-bound profile so your
                avatar initials and email in the header refresh
                immediately.
              </Step>
              <Step>
                You can also change your profile photo if avatar uploads
                are enabled in your build.
              </Step>
            </List>

            <Typography variant="subtitle1" sx={{ mt: 2 }}>
              Change Password tab
            </Typography>
            <List dense>
              <Step>
                Enter your <b>current password</b>, the new password
                (min. 8 chars) and confirm it.
              </Step>
              <Step>
                Click <b>Update</b>. On success you stay logged in but
                future logins must use the new password.
              </Step>
              <Step>
                If you can’t remember your current password, log out and
                use the <b>Forgot password?</b> flow described in
                section 1.
              </Step>
            </List>
          </AccordionDetails>
        </Accordion>

        {/* ============================================================ */}
        {/* 15. Troubleshooting                                          */}
        {/* ============================================================ */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <TopicHeader icon={<HelpOutlineIcon color="primary" />}>
              15. Troubleshooting / FAQ
            </TopicHeader>
          </AccordionSummary>
          <AccordionDetails>
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle2">
                "S3 credential check failed: An error occurred (403)"
              </Typography>
              <Typography variant="body2" color="text.secondary">
                The IAM user can authenticate but doesn't have permission
                on the bucket. Re-check the inline policy from the S3
                setup guide above and make sure both the bucket ARN and
                the <Code>/*</Code> object ARN are listed.
              </Typography>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle2">
                "S3 upload failed: NoSuchBucket"
              </Typography>
              <Typography variant="body2" color="text.secondary">
                The bucket name in the configuration doesn't match a
                real bucket in the configured region. Bucket names are
                global, but S3 routes requests by region – open the S3
                console and copy the exact name and region from there.
              </Typography>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle2">
                Playback opens but the browser can't read the URL
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Presigned URLs expire after six hours. Click{" "}
                <b>Refresh</b> in the Media tab to regenerate them. If
                you've embedded the URL into a third-party player, add
                the CORS configuration shown in the S3 setup section.
              </Typography>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle2">
                I can't find the Media / Users tab
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Both tabs are admin-only. If you're logged in as a
                regular user they will be hidden – ask the account owner
                to grant the admin role from the <b>Users</b> page.
              </Typography>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle2">
                Video upload stalls at 100% / file size error
              </Typography>
              <Typography variant="body2" color="text.secondary">
                The uploader chunks every file into 5&nbsp;MB pieces, so
                size shouldn't be a blocker. If a single chunk fails the
                whole upload aborts – check your connection and try
                again. The previous partial upload is cleaned up
                automatically.
              </Typography>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle2">
                Anytime webinar doesn’t resume from the last position
              </Typography>
              <Typography variant="body2" color="text.secondary">
                The cookie that stores progress is per-browser and
                per-domain. Make sure the user is on the same browser
                they watched on previously, hasn’t cleared cookies, and
                that <i>Anytime webinar</i> is still enabled in the
                webinar’s edit page. Click <b>Start from beginning</b>{" "}
                only if you actually want to wipe progress.
              </Typography>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2">
                Bulk ghost-user upload reports 0 created
              </Typography>
              <Typography variant="body2" color="text.secondary">
                The Excel/CSV must have a header row with at least{" "}
                <Code>first_name</Code> and <Code>last_name</Code>.
                Empty rows or rows missing both names are skipped –
                that’s usually what produced a “0 created” result.
              </Typography>
            </Paper>
          </AccordionDetails>
        </Accordion>
      </Box>
    </Layout>
  );
}

export default HelpPage;
